/* eslint no-undef: "warn" */
import React, { useEffect, useState } from 'react'

// Main Chakra UI components:
import {
    Box,
    Button,
    Center,
    HStack as ChakraHStack,
    Image,
    Link,
    List,
    ListItem,
    OrderedList,
    Text,
    UnorderedList,
    VStack,
} from '@chakra-ui/react'

import 'remixicon/fonts/remixicon.css'

import useMatchMedia from './MatchMedia'
import {
    actionColors,
    cursorColors,
    ActionBar,
    HStack,
    NotePane,
    SectionHeader,
    SubsectionHeader,
    RadialImage,
    RectImage,
    style,
} from './PlayersGuideUtil'

const PlayersGuideEquipment = () => {
    const isDesktopResolution = useMatchMedia('(min-width:992px)', true)

    return (
        <VStack mt={style.sectionSpacing} spacing={style.defaultSpacing}>
            <Center h={'75px'}>
                <SectionHeader id="equipment-and-items">Equipment and Items</SectionHeader>
            </Center>
            <HStack>
                <VStack style={{ width: isDesktopResolution ? '100%' : '100%' }}>
                    <Text>
                        Leveling up isn't about experience points in Mysterious Castle, it's about exploring the world, finding new
                        dungeons, and collecting better equipment and powerful new spells. As you explore, be on the lookout for 
                        treasure chests, hidden rooms, and secret passages that may contain powerful items to aid you on your journey.
                        <br />
                        <br />
                        Items come in two basic varieties: equipment and consumables. Equipment includes weapons, armor, and accessories that your characters can
                        wear to improve their stats and abilities. Consumables include potions and scrolls, which provide a situational boost when used, and
                        books or tomes, which permanently increase a character's skills or attributes when read.
                    </Text>
                    <NotePane>
                        <i className='ri-information-line ri-xl'></i> It's important to understand your characters' <i>roles</i> when assigning equipment,
                        and even more important when using consumable books and tomes. You only need one character to master <i>pick lock</i> for example,
                        so take some time at game start to understand your party members' strengths and weaknesses.
                    </NotePane>
                    <Text>
                        Some of the items you find in the game will be exceedingly rare, you could play a hundred runs and only find them once or twice. 
                        Don't count on finding these items, but when you do, make sure to use them wisely!
                    </Text>
                </VStack>
                <RectImage
                    src='/playersguide/equip.png'
                    alt='equipment screen'
                    maxWidth={isDesktopResolution ? '45%' : '100%'}
                    heading={''}
                />
            </HStack>
            <NotePane style={{ width: '100%' }}>
                    <Text>
                        <i className='ri-information-line ri-xl'></i> Your party leader will start with three very important items that can't be found 
                        anywhere else in the game, it's important that you keep these items safe, if you lose them, your adventure will be much more 
                        difficult!
                    </Text>
                    <List spacing={5} mt={5} mb={5}>
                        <ListItem>
                            <HStack inline>
                                <Image src='/playersguide/actions/long.png' />
                                <i>World Map</i>
                            </HStack>
                            <Text fontWeight={400}>
                                The world map shows you what parts of the map you have explored, and your current location. It will show you a map
                                of the area you are currently exploring, overworld, cave, or dungeon. If you hit a dead end or are blocked by powerful 
                                enemies, the world map will help you find another route.
                            </Text>
                        </ListItem>
                        <ListItem>
                            <HStack inline>
                                <Image src='/playersguide/actions/long.png' />
                                <i>Warp Orb</i>
                            </HStack>
                            <Text fontWeight={400}>
                                The warp orb allows you to teleport the party to any previously activated <i>warp stone</i> when you are not in combat. You 
                                will find warp stones scattered about the overworld and deep in caves. Until you find your first <i>warp stone</i>, the 
                                warp orb will not function.
                            </Text>
                        </ListItem>
                        <ListItem>
                            <HStack inline>
                                <Image src='/playersguide/actions/long.png' />
                                <i>Keyring</i>
                            </HStack>
                            <Text fontWeight={400}>
                                The keyring will hold all of the <i>keys</i> you find during your adventure. Keys are used to open locked doors and chests in 
                                dungeons. A skilled rogue can <i>pick locks</i> to open some locked doors and chests without using a key.
                            </Text>
                        </ListItem>
                    </List>
                    <Text>
                        Feel free to distribute these items to other party members to open up your leaders inventory, for example you might hand the <i>warp orb</i> to 
                        your mage and the <i>keyring</i> to your rogue.
                    </Text>
            </NotePane>
        </VStack>
    )
}

export default PlayersGuideEquipment
